import { Box } from '@material-ui/core';
import {
  BrandInformation,
  getMerchantDataById,
  patchSetBrandInformationAndUploadLogoToCDN,
} from '@nemuru-eng/js-api-client';
import { Dispatch, SetStateAction, useEffect } from 'react';

import { ImageUpload } from '@/components/modules/MerchantDetail/MerchantOverview/components/MerchantBranding/components/ImageUpload';
import { MerchantBrandingForm } from '@/components/modules/MerchantDetail/MerchantOverview/components/MerchantBranding/components/MerchantBrandingForm';
import { useBranding } from '@/components/modules/MerchantDetail/MerchantOverview/components/MerchantBranding/hooks/useBranding';
import {
  MerchantBrandingFormSchema,
  MerchantBrandingFormType,
} from '@/components/modules/MerchantDetail/MerchantOverview/components/MerchantBranding/schemas/MerchantBrandingFormSchema';
import { FieldVariant } from '@/components/shared/components/NewContextForms/types/FieldVariant';
import { EditDialogWrapper } from '@/components/shared/components/EditDialogWrapper';
import { Font } from '@/components/shared/components/Font';
import { Text } from '@/components/shared/components/Text';
import { useCache } from '@/hooks/requests/useCache';
import { useRequest } from '@/hooks/requests/useRequest';

interface MerchantBrandingEditDialogProps {
  opened: boolean;
  setOpened: Dispatch<SetStateAction<boolean>>;
  toggleDialog: () => void;
  merchantId: string;
  brandInformation?: BrandInformation;
}

export const MerchantBrandingEditDialog = ({
  opened,
  setOpened,
  toggleDialog,
  merchantId,
  brandInformation,
}: MerchantBrandingEditDialogProps) => {
  const { loading, brandLogoImageFile, setBrandLogoImageFile, resetBrandLogo } = useBranding();
  const { request, loading: requestLoading } = useRequest();
  const { revalidate } = useCache(getMerchantDataById, { merchantId });

  const merchantBrandingFormDefaultValues = {
    brandName: brandInformation?.brandName ?? '',
    customerSupportEmail: brandInformation?.customerSupportEmail ?? null,
    customerSupportPhoneNumber: brandInformation?.customerSupportPhoneNumber ?? null,
    homepageUrl: brandInformation?.homepageUrl ?? null,
  };

  useEffect(() => {
    if (!opened || !brandInformation) return;
    resetBrandLogo(brandInformation);
  }, [opened]);

  const onSubmitForm = async (useFormValues: MerchantBrandingFormType) => {
    if (!brandLogoImageFile) return;
    await request(patchSetBrandInformationAndUploadLogoToCDN, {
      id: merchantId,
      brandName: useFormValues.brandName,
      brandLogo: brandLogoImageFile.base64,
      ...(useFormValues.customerSupportEmail && { customerSupportEmail: useFormValues.customerSupportEmail }),
      ...(useFormValues.customerSupportPhoneNumber && {
        customerSupportPhoneNumber: useFormValues.customerSupportPhoneNumber,
      }),
      ...(useFormValues.homepageUrl && { homepageUrl: useFormValues.homepageUrl }),
    });
    toggleDialog();
    revalidate();
  };

  return (
    <EditDialogWrapper
      opened={opened}
      setOpened={setOpened}
      handleClose={toggleDialog}
      DialogProps={{
        maxWidth: 'sm',
        fullWidth: true,
      }}
      title={'EDIT_BRANDING_INFORMATION'}
      onSubmitForm={onSubmitForm}
      isLoading={requestLoading}
      formSchema={MerchantBrandingFormSchema}
      defaultValues={merchantBrandingFormDefaultValues}
      forceEnableSaveButton
    >
      <Box pt={2}>
        <MerchantBrandingForm brandInformation={brandInformation} fullWidth fieldVariant={FieldVariant.form} />
        <Box mt={2} />
        <Font variant={'body2'} color={'textSecondary'}>
          <Text tId={'BRAND_LOGO'} />
        </Font>
        <Box mt={2} />
        <ImageUpload
          name={'brandLogo'}
          loading={brandInformation && loading}
          defaultValue={brandLogoImageFile}
          onImageChange={setBrandLogoImageFile}
        />
      </Box>
    </EditDialogWrapper>
  );
};
